import { useState, useEffect, useRef } from 'react';
import vfs from '../../lib/virtualFS';
import { FolderOpen, File, Plus, Trash2, X, Upload } from 'lucide-react';

export default function FileExplorer({ activeFile, onFileSelect }) {
  const [files, setFiles] = useState(vfs.getFileList());
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    return vfs.subscribe(setFiles);
  }, []);

  const handleCreate = () => {
    if (newName.trim()) {
      const name = newName.endsWith('.py') ? newName : `${newName}.py`;
      vfs.writeFile(name, `# ${name}\n`);
      setNewName('');
      setCreating(false);
      onFileSelect(name);
    }
  };

  const handleDelete = (e, name) => {
    e.stopPropagation();
    vfs.deleteFile(name);
    if (activeFile === name) {
      const remaining = vfs.getFileList();
      onFileSelect(remaining[0] || null);
    }
  };

  const handleUpload = (e) => {
    const uploadedFiles = e.target.files;
    if (!uploadedFiles) return;
    Array.from(uploadedFiles).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        vfs.writeFile(file.name, ev.target.result);
        onFileSelect(file.name);
      };
      reader.readAsText(file);
    });
    e.target.value = '';
  };

  return (
    <div className="h-full flex flex-col bg-[#0a0a0a] border-b border-white/[0.06]" data-testid="file-explorer">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
        <div className="flex items-center gap-1.5 text-xs text-neutral-500 font-medium uppercase tracking-wider">
          <FolderOpen className="w-3.5 h-3.5" strokeWidth={1.5} />
          Files
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="text-neutral-500 hover:text-white transition-colors p-0.5"
            title="Upload file"
            data-testid="file-upload-button"
          >
            <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".py,.txt,.json,.csv,.md,.js,.html,.css,.yaml,.yml,.toml"
            onChange={handleUpload}
            className="hidden"
            data-testid="file-upload-input"
          />
          <button
            onClick={() => setCreating(true)}
            className="text-neutral-500 hover:text-white transition-colors p-0.5"
            data-testid="file-new-button"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-1">
        {creating && (
          <div className="flex items-center gap-1 px-2 py-1">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              className="flex-1 bg-white/5 border border-white/10 rounded text-xs text-white px-2 py-1 focus:outline-none focus:border-white/30"
              placeholder="filename.py"
              data-testid="file-new-input"
            />
            <button onClick={handleCreate} className="text-green-400 text-xs">OK</button>
            <button onClick={() => setCreating(false)} className="text-neutral-500"><X className="w-3 h-3" /></button>
          </div>
        )}

        {files.map((f) => (
          <div
            key={f}
            onClick={() => onFileSelect(f)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onFileSelect(f);
              }
            }}
            role="button"
            tabIndex={0}
            className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-xs transition-colors group ${
              activeFile === f
                ? 'bg-white/[0.06] text-white'
                : 'text-neutral-400 hover:bg-white/[0.03] hover:text-neutral-200'
            }`}
            data-testid={`file-item-${f}`}
          >
            <div className="flex items-center gap-2 truncate">
              <File className="w-3.5 h-3.5 flex-shrink-0 text-neutral-500" strokeWidth={1.5} />
              <span className="truncate font-mono">{f}</span>
            </div>
            <button
              onClick={(e) => handleDelete(e, f)}
              className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-red-400 transition-all"
              data-testid={`file-delete-${f}`}
            >
              <Trash2 className="w-3 h-3" strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
